import * as React from 'react';
import { ActivityFeedItem } from '../service/ActivityFeedItem';
import { EmptyStateView } from './EmptyStateView';
import { ActivityFeedActionType } from './ActivityFeedActions';
import { Link } from '@fluentui/react/lib/Link';
import { activityFeedClearAll } from './ActivityFeedView.locstring.json';
import ActivityFeedCard from './ActivityFeedCard';
import loc from 'owa-localize';
import { getActivityCardPresentationDetails } from '../utils/feedCardPresentationDetails';
import { Action } from 'adaptivecards';
import { toggleActivityFeedSettingPage } from '../actions/toggleActivityFeedSettingPage';
import { ActionButton } from '@fluentui/react/lib/Button';
import { notificationSettingTitle } from './ActivityFeedSettings.locstring.json';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';

export interface ActivityFeedViewProps {
    cards: ActivityFeedItem[];
    onAction?: (itemId: string, action: ActivityFeedActionType) => void;
    onAdaptiveCardAction?: (itemId: string, action: Action) => void;
    onDismissAll: () => void;
    isOpx?: boolean;
}

const styles = {
    base: require('./ActivityFeed.scss'),
    desktop: require('./ActivityFeed.outlookdesktop.scss'),
};
export function ActivityFeedViewV2(props: ActivityFeedViewProps) {
    const isFeedEmpty = props.cards.length === 0;
    const { getClass } = useClientStyles(styles);
    const settingsButtonClickHandler = React.useCallback(() => {
        toggleActivityFeedSettingPage(true);
    }, []);
    const cards = isFeedEmpty ? (
        <EmptyStateView />
    ) : (
        props.cards.map(feedItem => (
            <ActivityFeedCard
                key={feedItem.id}
                itemId={feedItem.id}
                item={getActivityCardPresentationDetails(feedItem)}
                onAction={props.onAction}
            />
        ))
    );
    const { ref } = useAutoCorrectContentHeightRef(props.isOpx);
    const onKeyPressClearAll = React.useCallback(event => {
        if (event.key == 'Tab' && event.shiftKey && props.isOpx) {
            let modal: HTMLInputElement = document.querySelector('#notificationSettingButtonId');
            modal.focus();
            event.preventDefault();
        }
    }, []);

    const onKeyPressNotificationSettingButton = React.useCallback(event => {
        if (event.key == 'Tab' && !event.shiftKey && props.isOpx) {
            let modal: HTMLInputElement = document.querySelector('#clearAllId');
            modal.focus();
            event.preventDefault();
        }
    }, []);

    return (
        <>
            <div className={getClass('header')}>
                <Link
                    id="clearAllId"
                    onKeyDown={onKeyPressClearAll}
                    className={getClass('clearAll')}
                    disabled={isFeedEmpty}
                    onClick={props.onDismissAll}>
                    {loc(activityFeedClearAll)}
                </Link>
            </div>
            <div ref={ref} className={getClass('feed')}>
                {cards}
            </div>
            <ActionButton
                id="notificationSettingButtonId"
                onKeyDown={onKeyPressNotificationSettingButton}
                className={getClass('notificationSettingButton')}
                styles={{
                    icon: getClass('backButtonIcon'),
                }}
                text={loc(notificationSettingTitle)}
                title={loc(notificationSettingTitle)}
                onClick={settingsButtonClickHandler}
            />
        </>
    );
}

// Added a heightUpdate callback so that the cardList height is updated on resize/initialize based on the window size.
function useAutoCorrectContentHeightRef(isOpx: boolean) {
    let activityFeedCardsRef = React.useRef(null);

    const setHeight = React.useCallback(() => {
        if (activityFeedCardsRef.current) {
            const height = isOpx ? window.innerHeight - 80 : window.innerHeight - 200;
            const heightStyle = height.toString() + 'px';
            activityFeedCardsRef.current.style.height = heightStyle;
            activityFeedCardsRef.current.style.maxHeight = heightStyle;
        }
    }, [activityFeedCardsRef.current]);

    React.useEffect(setHeight, [activityFeedCardsRef.current]);

    React.useEffect(() => {
        lazySubscribeToResizeEvent.importAndExecute(setHeight);
        return () => lazyUnsubscribeFromResizeEvent.importAndExecute(setHeight);
    }, []);

    return { ref: activityFeedCardsRef };
}
